//
// # Zoom-to-Box Mode
//
// Draw a rectangle on screen, then pan and zoom the viewport to fit that region.
//
// Two entry points:
// - `ZoomToBox.enter()`: mousedown already happened, anchor at current mouse position.
//   Drag to resize, mouseup to accept.
// - `ZoomToBoxWait.enter()`: keyboard entry (Z key), starts immediately at current
//   mouse position. Move to resize, left-click to accept, right-click/Esc to cancel.
//

// Local Imports
import type { InteractionHost } from "../interaction_host";
import { BoxDraw, BoxDrawWait } from "./box_draw";

// Keyboard entry (Z key): start zoom-to-box immediately at current mouse position.
// Left-click to accept, right-click or Esc to cancel.
export class ZoomToBoxWait extends BoxDrawWait {
  protected readonly inner: ZoomToBox;

  constructor(editor: InteractionHost) {
    super();
    this.inner = new ZoomToBox(
      editor,
      { ...editor.uiState.mousePos.sch },
      "Zoom to Box: left-click to accept, right-click to cancel"
    );
  }

  static enter(editor: InteractionHost): ZoomToBoxWait {
    const mode = new ZoomToBoxWait(editor);
    editor.setMode(mode);
    return mode;
  }
}

// Drag-based zoom-to-box (could be entered from mousedown).
export class ZoomToBox extends BoxDraw {
  constructor(
    editor: InteractionHost,
    anchor: { x: number; y: number },
    status: string = "Zoom to Box: drag to select region, Esc to cancel"
  ) {
    super(editor, anchor, "zoom-to-box", status);
  }

  // Enter zoom-to-box mode from a mouse-down event.
  static enter(editor: InteractionHost): ZoomToBox {
    const anchor = { ...editor.uiState.mousePos.sch };
    const mode = new ZoomToBox(editor, anchor);
    editor.setMode(mode);
    return mode;
  }

  // Finalize: compute bbox and fit the viewport to it.
  finalize(): void {
    const bbox = this.getBbox();
    this.editor.overlays.clearStatusMessage("zoom-to-box");
    this.editor.fitToBbox(bbox);
    this.editor.showFeedback("Zoomed to box");
    this.editor.goUiIdle();
  }

  override handleMouseUp = () => {
    this.finalize();
  };
}
